import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { takeUntil } from 'rxjs/operators';
import { LIST_FADE_ANIMATION } from 'src/app/shared/list.animation';
import { GlobalService } from 'src/app/shared/services/global.service';
import { WebsocketService } from 'src/app/shared/services/socket.service';
import { Utils } from 'src/app/shared/utils/utils';
import { BaseComponent } from 'src/app/core/base-classes/base/base.component';

export interface INotification {
  icon: string;
  name: string;
  time: string;
  url: string;
  read: boolean;
  colorClass: string;
  error: unknown;
  id?: string | number;
  createdAt?: string;
  taskMeta?: {
    message?: string;
    auditType?: string;
    auditId?: string | number;
    extendedView?: boolean;
    reviewAccess?: string;
    noUsers?: number;
    identityTrustScore?: boolean;
    error?: unknown;
  };
}

@Component({
    selector: 'fury-toolbar-notifications',
    templateUrl: './toolbar-notifications.component.html',
    styleUrls: ['./toolbar-notifications.component.scss'],
    animations: [...LIST_FADE_ANIMATION],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ToolbarNotificationsComponent extends BaseComponent implements OnInit {

  public notifications: INotification[] = [];
  /** Dismissed notifications, so a backlog resent after a reconnect doesn't bring them back. */
  private readonly dismissedKeys = new Set<string>();
  public isOpen: boolean;

  constructor(
    private _websocketService: WebsocketService,
    private _globalService: GlobalService,
    private _router: Router
  ) {
    super();
    this._websocketService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (notifications: INotification[]) => {
          notifications?.forEach(notification => {
            // The socket resends its backlog after every reconnect, so skip
            // anything already listed. The stored copy must keep the `id` for
            // this check to work (it used to be dropped, so every resend was
            // appended again and the list's track keys collided: NG0955).
            const key = notificationKey(notification);
            if (this.dismissedKeys.has(key) || this.notifications.some(n => notificationKey(n) === key)) {
              return;
            }
            this.notifications.push({
                id: notification.id,
                createdAt: notification?.createdAt,
                taskMeta: notification?.taskMeta,
                icon: 'notifications',
                name: notification?.taskMeta?.message,
                time: moment(Utils.formatDateTime(notification?.createdAt)).fromNow(),
                url: this.generateUrl(notification),
                read: false,
                colorClass: '',
                error: notification?.taskMeta?.error,
                // error: true,
              });
            // this.notifications = [...this.notifications];
          });
        }, complete: () => { }, error: () => { }
      });
  }

  generateUrl(notification: INotification): string {
    if (notification?.taskMeta?.auditType && notification?.taskMeta?.noUsers != 1) {
      const auditType = notification?.taskMeta?.auditType;
      const auditId = notification?.taskMeta?.auditId;
      const detailType = ((auditType == 'user' || auditType == 'resource') && notification?.taskMeta?.extendedView) ? 'detail-extended' : 'detail';
      const reviewAccess = notification?.taskMeta?.reviewAccess;
      return `/audit/${auditType}/${auditId}/${detailType}?reviewAccess=${reviewAccess}`;
    } else if (notification?.taskMeta?.identityTrustScore && notification.taskMeta.identityTrustScore == true && this._globalService.company.trialStatus == false) {
      return '/identity-trust-score';
    }
    return '';
  }

  ngOnInit() {
    this.updateTime();
  }

  markAsRead(notification: INotification, event: Event) {
    notification.read = true;
    this.dismiss(notification, event);
    this._router.navigateByUrl(notification.url);
    this.isOpen = false;
  }

  dismiss(notification: INotification, event: Event) {
    (event as MouseEvent).stopPropagation();
    this.dismissedKeys.add(notificationKey(notification));
    this.notifications.splice(this.notifications.indexOf(notification), 1);
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  onClickOutside() {
    this.isOpen = false;
  }

  markAllAsRead(event: Event) {
    this.notifications.forEach(n => this.dismissedKeys.add(notificationKey(n)));
    this.notifications = [];
  }

  updateTime() {
    this.notifications.forEach(notification => {
      notification.time = moment(Utils.formatDateTime(notification?.createdAt)).fromNow()
    });

  }
}

/** Identity of a notification: its id, or its message and time when it has none. */
function notificationKey(notification: any): string {
  if (notification?.id != null) {
    return `id:${notification.id}`;
  }
  return `msg:${notification?.taskMeta?.message ?? notification?.name ?? ''}|${notification?.createdAt ?? ''}`;
}
